import { Player, PlaylistEntry } from './../player';

export interface AdobeMediaTrackingConfig {
    platform: string;
    channel: string;
    debug?: boolean;
    mainInterval?: number;
    adInterval?: number;
    customMetadataCallback?: (contextData: Record<string, string | number>) => Record<string, string | number>;
}

export class AdobeMediaTrackingManager {
    private static PLAYER_NAME = 'Corus Video Player';
    private static LIVE_STREAM_DURATION: number = 86400;

    private config: AdobeMediaTrackingConfig | null = null;
    private debugLogging: boolean = false;
    private sessionId: string | null = null;

    private mediaStateInterval: ReturnType<typeof setInterval> | null = null;
    private mediaStateTimeout: ReturnType<typeof setTimeout> | null = null;

    // Start DAI ad pod count at 10 to distinguish between VOD ad pods.
    private daiPodCount: number = 10;

    public constructor(
        private player: Player,
        private $window: AlloyWindow = window as AlloyWindow
    ) { }

    public setup(config?: AdobeMediaTrackingConfig): void {
        if (!config) {
            console.log('No config provided');
            return;
        }

        if (!this.$window.alloy) {
            console.log('Adobe Experience Platform library not found');
            return;
        }


        this.$window.alloy('getLibraryInfo',null).then(result => {
            // libraryInfo contains config values pulled from the configured instance of the Web SDK
            const { libraryInfo } = result;
            config.mainInterval = libraryInfo.configs?.streamingMedia?.mainPingInterval ?? 10;
            config.adInterval   = libraryInfo.configs?.streamingMedia?.adPingInterval ?? 10;
        });

        this.debugLogging = Boolean(config.debug);
        this.config = config;

        if (this.debugLogging) {
            console.log('Adobe Experience Platform Initialized');
        }
    }

    /**
     * Tracking video on initial start
     * @returns Promise<void>
     */
    public async trackVideoLoad(): Promise<void> {
        if (!this.config) return;
        if (!this.$window.alloy) return;

        const index = this.player.playlistIndex || 0;
        const currentMedia = <PlaylistEntry> this.player.config.jwOptions.playlist[index];

        if (!currentMedia) return;

        try {
            // Get media session details
            const mediaSessionDetails = this.getMediaSessionDetails();

            // Get initial context data
            let processedContextData = this.getVideoContextData(currentMedia);

            // Additional context modifications
            processedContextData = this.getCustomMetadata(processedContextData);

            // Convert context data to custom metadata array
            const customMetadata: CustomMetadata[] = Object.entries(processedContextData).map(([name, value]) => ({
                name,
                value: value.toString()
            }));

            // Analytics data for shuffle play event
            const shuffleSequence = JSON.parse(localStorage.getItem('shuffleSequence') || '0');
            if (shuffleSequence === 0) {  // checkout for first shuffle play event
                customMetadata.push({
                    name: 'video.random',
                    value: '1'
                });
            }

            const urlParams = new URLSearchParams(window.location.search);
            const isSmartPlay = urlParams.get('action') === 'smartPlay';

            // Analytics data for smartplay event
            if (isSmartPlay) {
                customMetadata.push({
                    name: 'video.smartplay',
                    value: '1'
                });
            }

            // Analytics data for binge watch event
            const savedItem = JSON.parse(localStorage.getItem('items') || '{}');
            if (savedItem?.bingeWatched > 1) {
                customMetadata.push({
                    name: 'video.binge',
                    value: (savedItem.bingeWatched - 1).toString()
                });
            }

            // Create a session
            const sessionStartResult = await this.$window.alloy('createMediaSession', {
                xdm: {
                    eventType: 'media.sessionStart',
                    mediaCollection: {
                        sessionDetails: mediaSessionDetails,
                        customMetadata: customMetadata,
                        playhead: 0
                    }
                }
            });

            // Set the session ID
            this.sessionId = sessionStartResult.sessionId;

            if (this.sessionId && this.sessionId !== null) {
                // Send play event immediately after session start
                await this.sendMediaEvent('media.play');
            }

            if (this.debugLogging) {
                console.log('Media session started', sessionStartResult);
            }
        } catch (error) {
            console.error('Error tracking video load', error);
        }
    }

    /**
     * Send media event to tracking system
     * @param eventType Type of media event to track
     * @param additionalParams Additional parameters to send under mediaCollection
     * @param extraPayload Optional extra payload to merge at the root of the xdm object
     * @returns Promise<void>
     */
    private async sendMediaEvent(
        eventType: string,
        additionalParams: Record<string, any> = {},
        extraPayload: Record<string, any> = {}
    ): Promise<void> {
        if (!this.config || !this.sessionId || !this.$window.alloy) return;
        const timeElapsedFromMidnight = Math.floor(Date.now() / 1000) % 86400;
        const playerPosition = this.player.currentMedia.metadata?.liveStream ? timeElapsedFromMidnight : Math.round(this.player.currentMediaPosition);
        try {
            await this.$window.alloy('sendMediaEvent', {
                xdm: {
                    eventType: eventType,
                    mediaCollection: {
                        playhead: playerPosition,
                        sessionID: this.sessionId,
                        ...additionalParams
                    },
                    ...extraPayload
                }
            });
        } catch (error) {
            console.error(`Error tracking ${eventType}`, error);
        }
    }

    /**
     * Track video play
     * @returns Promise<void>
     */
    public async trackPlay(): Promise<void> {
        await this.sendMediaEvent('media.play');
        this.startMediaStatePings('main');
    }

    /**
     * Track video pause
     * @returns Promise<void>
     */
    public async trackPause(): Promise<void> {
        await this.sendMediaEvent('media.pauseStart');
        this.stopMediaStatePings();
    }

    /**
     * Track video error
     * @returns Promise<void>
     */
    public async trackVideoError(error: any): Promise<void> {
        const errorString = AdobeMediaTrackingManager.errorToString(error);
        await this.sendMediaEvent('media.error', {
            errorDetails: {
                name: errorString,
                source: AdobeMediaTrackingManager.PLAYER_NAME,
            }
        });
        this.trackVideoUnload();
    }

    /** Track immediately closing the session
     * @returns Promise<void>
     */
    public async trackVideoUnload(): Promise<void> {
        await this.sendMediaEvent('media.sessionEnd');
        this.stopMediaStatePings();
    }

    /**
     * Track chapter start
     * @returns Promise<void>
     */
    public async trackChapterStart(): Promise<void> {
        await this.sendMediaEvent('media.chapterStart', {
            chapterDetails: {
                friendlyName: `Chapter ${this.player.currentChapter}`,
                length: this.player.currentChapterDuration,
                index: this.player.currentChapter,
                offset: this.player.currentChapterOffset
            }
        });
    }

    /**
     * Track chapter complete
     * @returns Promise<void>
     */
    public async trackChapterComplete(): Promise<void> {
        await this.sendMediaEvent('media.chapterComplete');
    }

    /**
     * Track when session ended
     * @returns Promise<void>
     */
    public async trackComplete(): Promise<void> {
        await this.sendMediaEvent('media.sessionComplete');
        this.stopMediaStatePings();
    }

    /**
     * Track buffer start
     * @returns Promise<void>
     */
    public async trackBufferStart(): Promise<void> {
        await this.sendMediaEvent('media.bufferStart');
    }

    /**
     * Track state update
     * @returns Promise<void>
     */
    public async trackStateUpdate(): Promise<void> {
        await this.sendMediaEvent('media.statesUpdate');
    }

    /**
     * Track Ad start
     * @returns Promise<void>
     */
    public async trackAdStart(ad: any, isNewBreak: boolean = false): Promise<void> {
        let adId: string = '';
        let podIndex: number = 0;
        let adLength: number = 0;
        let adPosition: number = 0;

        if (ad.ima && ad.ima.ad) {
            // Retrieve ad info via ad.ima SDK
            const imaAd: any = ad.ima.ad;
            const podInfo: ImaAdPodInfo = imaAd.getAdPodInfo();

            adId = imaAd.getAdId();
            podIndex = podInfo.getPodIndex() + 1;
            adLength = Math.max(0, imaAd.getDuration());
            adPosition = podInfo.getAdPosition();
        } else if ('dai' === ad.client) {
            // DAI ad values.
            adId = ad.id;
            adLength = ad.duration;

            // In case ad.sequence returned 0
            adPosition = ad.sequence ? ad.sequence : 1;

            // Pod number returns the same number for all pods.
            // Keep track of ad pod number manually.
            podIndex = this.daiPodCount;

            if (isNewBreak) {
                this.daiPodCount++;
            }
        }

        if (isNewBreak) {
            await this.sendMediaEvent('media.adBreakStart', {
                advertisingPodDetails: {
                    friendlyName: ad.adtitle ?? 'NA',
                    offset: 0,
                    index: podIndex
                }
            });
        }

        await this.sendMediaEvent('media.adStart', {
            advertisingDetails: {
                friendlyName: ad.adtitle ?? 'NA',
                name: adId ?? 'NA',
                length: Math.round(adLength) ?? 'NA',
                creativeID: ad.creativeAdId ?? 'NA',
                podPosition: adPosition ?? 'NA',
                advertiser: ad.advertiser ?? 'NA',
                playerName: AdobeMediaTrackingManager.PLAYER_NAME
            },
        });

        // Send play event to better track time spent
        await this.sendMediaEvent('media.play');
        this.startMediaStatePings('ad');
    }

    /**
     * Track Ad complete
     * @returns Promise<void>
     */
    public async trackAdComplete(): Promise<void> {
        await this.sendMediaEvent('media.adComplete');
        this.stopMediaStatePings();
        this.startMediaStatePings('main');
    }

    /**
     * Track Ad break complete
     * @returns Promise<void>
     */
    public async trackAdBreakComplete(): Promise<void> {
        await this.sendMediaEvent('media.adBreakComplete');
        // Send play event to better track time spent
        await this.sendMediaEvent('media.play');
        this.stopMediaStatePings();
        this.startMediaStatePings('main');
    }

    /**
     * Start media state pings
     * This will ping the media state every 10 seconds after an initial 10 second delay
     * @returns void
     */
    private startMediaStatePings(type: 'ad' | 'main' = 'main'): void {
        this.stopMediaStatePings();

        // by default, irrespective of type, the ping should fire at 10 sec interval
        let interval: number = 10000;
        if (this.config) {
            interval = (type === 'ad' ? this.config.adInterval : this.config.mainInterval ) * 1000;  // in millisec
        }


        // First ping after 10s for main media and for ad media
        this.mediaStateTimeout = setTimeout(async () => {
            await this.trackMediaState();
            // Then every 10s or 1s depending on type
            this.mediaStateInterval = setInterval(() => {
                this.trackMediaState();
            }, interval);
        }, interval);
    }

    /**
     * Stop media state pings
     * @returns void
     */
    private stopMediaStatePings(): void {
        if (this.mediaStateTimeout) {
            clearTimeout(this.mediaStateTimeout);
            this.mediaStateTimeout = null;
        }
        if (this.mediaStateInterval) {
            clearInterval(this.mediaStateInterval);
            this.mediaStateInterval = null;
        }
    }

    /**
     * Track media state
     * @returns Promise<void>
     */
    public async trackMediaState(): Promise<void> {
        await this.sendMediaEvent('media.ping', {}, {
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Get custom metadata
     * @param contextData
     * @returns
     */
    private getCustomMetadata(contextData: Record<string, string | number> = {}): Record<string, string | number> {
        if (this.config.customMetadataCallback) {
            contextData = this.config.customMetadataCallback(contextData);
        }

        return contextData;
    }

    /**
     * Get media session details, only including the required fields
     * @returns sessionDetails media session details
     */
    public getMediaSessionDetails(): SessionDetails {
        const streamType = this.player.currentMedia.metadata.liveStream ? 'live' : 'vod';
        const playerName = AdobeMediaTrackingManager.PLAYER_NAME;

        const sessionDetails: SessionDetails = {
            contentType: streamType,
            channel: this.config.platform ? this.config.platform : this.config.channel,
            name: this.player.currentMediaData?.mediaId,
            friendlyName: this.player?.currentMediaMetaData,
            show: this.player.currentMediaData?.show,
            length: this.player.currentMedia.metadata?.liveStream
                ? AdobeMediaTrackingManager.LIVE_STREAM_DURATION
                : Math.round(this.player?.currentMediaDuration),
            playerName: playerName,
        };
        return sessionDetails;
    }

    /**
     * Return additional data to pass when creating the session
     * @param currentMedia
     * @returns contextData
     */
    public getVideoContextData(currentMedia: PlaylistEntry): Record<string, string | number> {
        const contextData: Record<string, string | number> = {
            'video.episodeName': currentMedia.title ?? 'NA',
            'video.show': currentMedia.show ?? 'NA',
            'video.episodeID': currentMedia.metadata.episodeID || 0,
            'video.type': currentMedia.metadata.clipType ?? 'NA',
            'video.genre': currentMedia.metadata.genre ?? 'NA',
            'video.channel': this.config.channel ?? 'NA',
        };

        const episodeNumber = parseInt(currentMedia.metadata.episodeNumber, 10);
        if (!isNaN(episodeNumber)) {
            contextData['video.episode'] = episodeNumber;
        }

        const seasonNumber = parseInt(currentMedia.metadata.seasonNumber, 10);
        if (!isNaN(seasonNumber)) {
            contextData['video.season'] = seasonNumber;
        }

        // AB Testing tracking
        if (this.$window['abTest']) {
            for (const experiment in this.$window['abTest']) {
                contextData['abtest.' + experiment] = this.$window['abTest'][experiment].toString();
            }
        }

        // Ad Block tracking
        contextData['video.adblocked'] = this.player.isAdBlocked.toString();

        return contextData;
    }

    /**
     * Convert error to string
     * @param error error object or message
     * @returns error error message as string
     */
    private static errorToString(error: any): string {
        if (!error) return 'Unknown error';
        if (typeof error === 'string') return error;
        if (error instanceof Error) return error.message;
        try {
            return JSON.stringify(error);
        } catch {
            return String(error);
        }
    }

    /**
     * Check if Adobe Experience Platform library is ready
     * @returns boolean
     */
    public isAdobeLibraryReady(): boolean {
        return this.$window.alloy !== undefined;
    }

    /**
     * Check if the session is ready
     * @returns boolean
     */
    public isSessionReady(): boolean {
        return this.sessionId !== null && this.config !== null && this.isAdobeLibraryReady();
    }
}
